import { useState, useEffect, type ChangeEvent } from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { Box } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { formatDate } from "../../../utils/deteUtils";
import { getAsyncTasks } from "../../../api";
import type { AsyncTaskItem } from "../../../types/async-task";

type ImportLogColumnId =
  | "create_at"
  | "stat"
  | "complete_at"
  | "message_id";

type ImportLogColumn = {
  id: ImportLogColumnId;
  label: string;
  minWidth: string;
  align?: "left" | "right" | "center";
  isSmallScreen: boolean;
};

const columns: ImportLogColumn[] = [
  {
    id: "create_at",
    label: "インポート日時",
    minWidth: "fit-content",
    isSmallScreen: true,
  },
  {
    id: "stat",
    label: "ステータス",
    minWidth: "fit-content",
    align: "left",
    isSmallScreen: true,
  },
  {
    id: "complete_at",
    label: "完了日時",
    minWidth: "fit-content",
    align: "left",
    isSmallScreen: false,
  },
  {
    id: "message_id",
    label: "メッセージ",
    minWidth: "fit-content",
    align: "left",
    isSmallScreen: false,
  },
];

export default function ImportLog() {
  const [selectedRow, setSelectedRow] = useState<AsyncTaskItem | null>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const handleLocationCodeClick = (row: AsyncTaskItem) => {
    setSelectedRow(row);
    setIsPopoverOpen(true);
  };

  const handlePopoverClose = () => {
    setIsPopoverOpen(false);
  };

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const [ssmasters, setSsMasters] = useState<AsyncTaskItem[]>([]);
  const [totalCount, setTotalCount] = useState<number | undefined>();
  const getSSMaster = async () => {
    try {
      const response = await getAsyncTasks({
        kind: "importSSMaster",
        stat: null,
        order: null,
        sort: null,
        page: page + 1,
        limit: rowsPerPage,
      });
      if (response.status === "ok") {
        const data = response.async_tasks;
        const count = response.total_count;
        console.log("SS基本台帳取得", response);
        setSsMasters(data);
        setTotalCount(count);
      }
    } catch (error) {
      console.error("API呼び出しエラー1", error);
    }
  };

  useEffect(() => {
    getSSMaster();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage, page]);

  return (
    <>
      <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none" }}>
        <TableContainer>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{ minWidth: column.minWidth }}
                    sx={{
                      padding: "8px",
                      fontWeight: "bold",
                      borderBottom: 2,
                      borderBottomColor: "outline.dark",
                      "@media screen and (max-width:900px)": {
                        display:
                          column.isSmallScreen === true ? "table-cell" : "none",
                      },
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {ssmasters?.map((row) => {
                return (
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={row.id}
                    onClick={() => handleLocationCodeClick(row)}
                    sx={{
                      pointerEvents: "none",
                      "@media screen and (max-width:900px)": {
                        cursor: "pointer",
                        pointerEvents: "auto",
                      },
                    }}
                  >
                    {columns.map((column) => {
                      const value = row[column.id];
                      return (
                        <TableCell
                          key={column.id}
                          align={column.align}
                          sx={{
                            padding: "8px",
                            "@media screen and (max-width:900px)": {
                              display:
                                column.isSmallScreen === true
                                  ? "table-cell"
                                  : "none",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "32px",
                            }}
                          >
                            {column.id === "stat" ? (
                              row.stat === 0 ? (
                                "実行中"
                              ) : row.stat === 1 ? (
                                "正常終了"
                              ) : row.stat === 9 ? (
                                "異常終了"
                              ) : null
                            ) : column.id === "create_at" ||
                              column.id === "complete_at" ? (
                              formatDate(row[column.id])
                            ) : (
                              <>{value}</>
                            )}
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Dialog open={isPopoverOpen} onClose={handlePopoverClose}>
          <Box
            sx={{
              display: "flex",
              height: "56px",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px",
              borderBottom: "2px solid #CCC",
            }}
          >
            <Box
              sx={{
                paddingLeft: "8px",
                fontWeight: "bold",
                fontSize: "20px",
              }}
            >
              アカウント詳細
            </Box>
            <IconButton
              aria-label="close"
              onClick={handlePopoverClose}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Table sx={{ WebkitWritingMode: "horizontal-tb" }}>
            <TableBody>
              {selectedRow &&
                columns.map((column) => {
                  return (
                    <TableRow role="checkbox" tabIndex={-1} key={column.id}>
                      <TableCell
                        align={column.align}
                        sx={{
                          color: "outline.dark",
                          fontWeight: "bold",
                          boxSizing: "border-box",
                          width: "calc((100vw - 64px) / 2)",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            WebkitWritingMode: "horizontal-tb",
                            height: "100%",
                          }}
                        >
                          {column.label}
                        </Box>
                      </TableCell>
                      <TableCell
                        align={column.align}
                        sx={{
                          writingMode: "horizontal-tb",
                          boxSizing: "border-box",
                          width: "calc((100vw - 64px) / 2)",
                          overflowWrap: "break-word",
                        }}
                      >
                        {column.id === "create_at" ||
                        column.id === "complete_at" ? (
                          formatDate(String(selectedRow[column.id] ?? ""))
                        ) : (
                          <>{selectedRow[column.id]}</>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </Dialog>
        <TablePagination
          rowsPerPageOptions={[10, 30, 50, 100]}
          component="div"
          //totalDataがnull、undefined時に対応
          count={totalCount ?? 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            ".MuiToolbar-root,.MuiInputBase-root,.MuiTablePagination-actions": {
              margin: "0 !important",
              padding: "0 !important",
            },
          }}
        />
      </Paper>
    </>
  );
}
